import type { ClassNameProp } from '@ps/web-shared-components';
import { get, isUndefined } from 'lodash';
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import styles from './LazyImage.scss';

export interface Props extends ClassNameProp {
  alt?: string;
  fallback?: React.ReactNode;
  loader?: React.ReactNode;
  src: string;
  visible?: boolean;
}

enum Status {
  PRISTINE,
  PENDING,
  SUCCESS,
  FAILURE
}

const cache: Record<string, { observable: Observable<unknown>; status: Status }> = {};

const load = (src: string) => {
  if (!cache[src]) {
    cache[src] = {
      observable: new Observable((subscriber) => {
        const imgElem = new Image();
        imgElem.onload = () => {
          cache[src].status = Status.SUCCESS;
          subscriber.complete();
        };
        imgElem.onerror = () => {
          delete cache[src];
          subscriber.error();
        };
        imgElem.src = src;
      }).pipe(shareReplay({ bufferSize: 1, refCount: false })),
      status: Status.PRISTINE
    };
  }
  return cache[src];
};

const LazyImage: React.FC<Props> = ({
  visible,
  src,
  alt,
  fallback = <img width="1px" height="1px" alt="" />,
  loader = <div className={styles.loader} />,
  ...rest
}) => {
  const [status, setStatus] = useState(get(cache, [src, 'status'], Status.PRISTINE));
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0 });

  useEffect(() => {
    if (
      !process.env.STORYBOOK_TEST_RUNNER &&
      (visible || inView) &&
      [Status.PRISTINE, Status.PENDING].includes(status)
    ) {
      setStatus(Status.PENDING);
      const subscription = load(src).observable.subscribe({
        complete: () => setStatus(Status.SUCCESS),
        error: () => setStatus(Status.FAILURE)
      });
      return () => subscription.unsubscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, inView, src]);

  let children: ReactNode;
  switch (status) {
    case Status.SUCCESS:
      return <img src={src} {...rest} alt={alt ?? 'image'} />;
    case Status.PRISTINE:
    case Status.PENDING:
      children = loader || fallback;
      break;
    case Status.FAILURE:
      children = fallback;
      break;
  }

  return isUndefined(visible) ? <div ref={ref}>{children}</div> : children;
};

export default LazyImage;
